import { useMemo, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField as MuiTextField,
} from "@mui/material";

import type { SupersetDashboard } from "@thalamos/aspire-common";

import { apiHooks } from "../../api.js";

interface SupersetDashboardPickerProps {
  value: string;
  onChange: (supersetDashboardId: string) => void;
}

export interface SupersetDashboardPickerInnerProps {
  value: string;
  onChange: (supersetDashboardId: string) => void;
  options: SupersetDashboard[];
  loading: boolean;
  onSearchChange: (query: string) => void;
}

export function SupersetDashboardPickerInner({
  value,
  onChange,
  options,
  loading,
  onSearchChange,
}: SupersetDashboardPickerInnerProps) {
  const selectedOption = options.find((d) => d.id === value) ?? null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) =>
        `${option.displayName} (ID: ${option.dashboardId})`
      }
      value={selectedOption}
      onChange={(_event, newValue) => {
        onChange(newValue?.id ?? "");
      }}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === "input") {
          onSearchChange(newInputValue);
        }
      }}
      loading={loading}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label="Superset Dashboard"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

export function SupersetDashboardPicker({
  value,
  onChange,
}: SupersetDashboardPickerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [{ data, loading }] = apiHooks.supersetDashboards.search(
    searchQuery,
    50,
    0,
  );

  // Fetch the currently selected dashboard by ID so it always appears in the
  // options list, even when it falls outside the first page of search results.
  const [{ data: selectedDashboard }] = apiHooks.supersetDashboards.get(value);

  const searchResults = data?.results;

  const options = useMemo(() => {
    const results: SupersetDashboard[] = searchResults ?? [];
    if (
      !value ||
      !selectedDashboard ||
      results.some((d) => d.id === selectedDashboard.id)
    ) {
      return results;
    }
    return [selectedDashboard, ...results];
  }, [searchResults, selectedDashboard, value]);

  return (
    <SupersetDashboardPickerInner
      value={value}
      onChange={onChange}
      options={options}
      loading={loading}
      onSearchChange={setSearchQuery}
    />
  );
}
